import React, { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { ClipboardCheck } from "lucide-react";

/** Backend CheckInPayload — ALL SIX FIELDS ARE REQUIRED INTEGERS (0..10):
 *  mood, anxiety, craving, sleep_quality, social_energy, stress + optional note. */
const FIELDS = [
  { key: "mood",         en: "Mood",          nl: "Stemming" },
  { key: "anxiety",      en: "Anxiety",       nl: "Angst" },
  { key: "craving",      en: "Craving",       nl: "Verlangen" },
  { key: "sleep_quality", en: "Sleep quality", nl: "Slaapkwaliteit" },
  { key: "social_energy", en: "Social energy", nl: "Sociale energie" },
  { key: "stress",       en: "Stress",        nl: "Spanning" },
];

export default function CheckIn() {
  const t = useT();
  const qc = useQueryClient();
  const [values, setValues] = useState({
    mood: 5, anxiety: 3, craving: 2, sleep_quality: 6, social_energy: 5, stress: 4,
  });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const list = useQuery({
    queryKey: ["checkins"],
    queryFn: async () => (await api.get("/checkins")).data,
    retry: false,
  });
  const items = Array.isArray(list.data) ? list.data : list.data?.items || [];

  const submit = async () => {
    setBusy(true);
    try {
      // Send exactly the shape the backend requires. Every value must be an int.
      const payload = {
        mood: Number(values.mood),
        anxiety: Number(values.anxiety),
        craving: Number(values.craving),
        sleep_quality: Number(values.sleep_quality),
        social_energy: Number(values.social_energy),
        stress: Number(values.stress),
        note: note.trim() || null,
      };
      await api.post("/checkins", payload);
      toast.success(t("common.saved"));
      setNote("");
      qc.invalidateQueries({ queryKey: ["checkins"] });
    } catch (e) {
      toast.error(errMsg(e, t("errors.generic")));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8">
      <PageHeader eyebrow={t("nav.checkins")} title={t("client.quickCheckin")} subtitle={t("brand.tagline")} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-3xl bg-white shadow-card p-6 md:p-8">
          <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-500 mb-4">
            {t("client.greeting")}
          </div>

          <div className="space-y-5" data-testid="checkin-sliders">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-ink-700 font-medium">{f.en}</div>
                  <div className="text-sm text-ink-900 font-mono">{values[f.key]}<span className="text-ink-500">/10</span></div>
                </div>
                <Slider
                  value={[values[f.key]]}
                  min={0}
                  max={10}
                  step={1}
                  onValueChange={(v) => setValues((s) => ({ ...s, [f.key]: v[0] }))}
                  data-testid={`checkin-slider-${f.key}`}
                />
              </div>
            ))}
          </div>

          <div className="mt-6">
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("client.typeMessage")}
              rows={3}
              className="rounded-xl bg-cream-50"
              data-testid="checkin-note"
            />
          </div>

          <div className="mt-6 flex justify-end">
            <Button
              onClick={submit}
              data-testid="checkin-submit"
              disabled={busy}
              className="rounded-full bg-sage-500 hover:bg-sage-600 text-white h-11 px-6"
            >
              {busy ? t("common.saving") : t("common.save")}
            </Button>
          </div>
        </div>

        <div className="rounded-3xl bg-cream-50 border border-border p-6">
          <div className="flex items-center gap-2 mb-3">
            <ClipboardCheck className="h-4 w-4 text-sage-600" />
            <div className="font-heading text-ink-900">Recent</div>
          </div>
          {list.isLoading && <LoadingCard height="h-16" />}
          {list.isError && <ErrorState message={errMsg(list.error)} onRetry={() => list.refetch()} />}
          {!list.isLoading && items.length === 0 && (
            <div className="text-sm text-ink-500">{t("common.empty")}</div>
          )}
          <div className="space-y-2" data-testid="checkin-history">
            {items.slice(0, 8).map((c, i) => (
              <div key={c.id || i} className="rounded-xl bg-white p-3 border border-border">
                <div className="text-xs text-ink-500">
                  {c.created_at ? new Date(c.created_at).toLocaleString() : ""}
                </div>
                <div className="text-xs text-ink-900 mt-1 font-mono">
                  {["mood","anxiety","craving","sleep_quality","social_energy","stress"]
                    .filter((k) => typeof c[k] === "number")
                    .map((k) => `${k}:${c[k]}`).join(" · ") || "—"}
                </div>
                {c.note && <div className="text-sm text-ink-700 mt-1">{c.note}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
